'use client';

import Link from 'next/link';
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { Heart, Sparkles, Swords, ThumbsDown } from 'lucide-react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useTranslations } from 'next-intl';

import { ProductMedia } from '@/components/product-media';
import { Button } from '@/components/ui/button';
import { getCatalogEmoji } from '@/hooks/use-product-card-actions';
import {
  selectExperiencePreviewProducts,
  type ExperiencePreviewProduct,
} from '@/lib/experienceFeedPreviewProducts';
import type { Product } from '@/lib/productService';
import { cn, formatCurrency } from '@/lib/utils';

const SCENES = ['feed', 'swipe', 'battle'] as const;
const PREVIEW_ANIMATION_DURATION_S = 3.2;
const FEED_ANIMATION_DURATION_S = 7.5;
const SCENE_TRANSITION_DURATION_S = 0.2;
/** Extra still time after the in-scene animation finishes (swipe gets the longest hold). */
const SCENE_POST_ANIMATION_HOLD_MS: Record<(typeof SCENES)[number], number> = {
  feed: 1000,
  swipe: 2400,
  battle: 900,
};

function getSceneDurationMs(scene: (typeof SCENES)[number]): number {
  const animationMs =
    scene === 'feed'
      ? Math.round(FEED_ANIMATION_DURATION_S * 1000)
      : Math.round(PREVIEW_ANIMATION_DURATION_S * 1000);
  return animationMs + SCENE_POST_ANIMATION_HOLD_MS[scene];
}

type PreviewScene = (typeof SCENES)[number];

type ExperienceFeedPreviewBannerProps = {
  normalizedLocale: string;
  publicStoreId: string;
  locale: string;
  products: Product[];
  className?: string;
};

function subscribeToReducedMotion(onStoreChange: () => void) {
  const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  mediaQuery.addEventListener('change', onStoreChange);
  return () => mediaQuery.removeEventListener('change', onStoreChange);
}

function getReducedMotionSnapshot() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

function PreviewMiniCard({
  product,
  locale,
  className,
  compact = false,
  fill = false,
  overlayDetails = false,
}: {
  product: ExperiencePreviewProduct;
  locale: string;
  className?: string;
  compact?: boolean;
  fill?: boolean;
  overlayDetails?: boolean;
}) {
  const catalogType = product.catalogType ?? 'FASHION';
  const hasPromotion =
    product.promotionPrice != null &&
    product.promotionPrice > 0 &&
    product.price > 0 &&
    product.promotionPrice < product.price;
  const displayPrice = hasPromotion ? product.promotionPrice! : product.price;
  const showPrice = displayPrice > 0;

  return (
    <div
      className={cn(
        'overflow-hidden rounded-2xl border border-white/15 bg-background/95 shadow-lg backdrop-blur-sm',
        fill && 'h-full',
        fill && !overlayDetails && 'flex flex-col',
        overlayDetails && 'relative',
        className
      )}
    >
      <div
        className={cn(
          'relative w-full bg-muted',
          fill && !overlayDetails
            ? 'min-h-0 flex-1'
            : compact
              ? 'aspect-[4/5]'
              : 'aspect-[3/4]',
          overlayDetails && 'absolute inset-0'
        )}
      >
        {product.imageUrl && !product.isPlaceholder ? (
          <ProductMedia
            src={product.imageUrl}
            alt={product.name || 'Product'}
            fill
            className="object-cover"
            sizes="160px"
          />
        ) : (
          <div className="flex h-full w-full items-center justify-center bg-muted">
            <span className={compact ? 'text-3xl' : 'text-4xl'}>
              {getCatalogEmoji(catalogType)}
            </span>
          </div>
        )}
      </div>
      <div
        className={cn(
          'space-y-0.5',
          compact ? 'p-2' : 'p-2.5',
          overlayDetails &&
          'absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/45 to-transparent p-3 pt-8 text-white'
        )}
      >
        <p
          className={cn(
            'truncate font-medium text-foreground',
            compact ? 'text-[10px]' : 'text-xs',
            overlayDetails && 'text-white'
          )}
        >
          {product.name || '—'}
        </p>
        {showPrice ? (
          <p
            className={cn(
              'font-semibold text-primary',
              compact ? 'text-[10px]' : 'text-xs',
              overlayDetails && 'text-white'
            )}
          >
            {formatCurrency(displayPrice, locale)}
          </p>
        ) : null}
      </div>
    </div>
  );
}

function FeedScene({
  products,
  locale,
  animate,
}: {
  products: ExperiencePreviewProduct[];
  locale: string;
  animate: boolean;
}) {
  const feedProducts = products.slice(0, 4);

  return (
    <div className="relative h-full overflow-hidden bg-background">
      <motion.div
        className="h-[400%]"
        animate={
          animate
            ? {
              y: ['0%', '0%', '-25%', '-25%', '-50%', '-50%', '-75%', '-75%', '0%'],
            }
            : undefined
        }
        transition={
          animate
            ? {
              duration: FEED_ANIMATION_DURATION_S,
              ease: 'easeInOut',
              times: [0, 0.15, 0.23, 0.38, 0.46, 0.61, 0.69, 0.84, 1],
            }
            : undefined
        }
      >
        {feedProducts.map((product, index) => (
          <div key={`${product.id}-${index}`} className="h-1/4 p-3">
            <PreviewMiniCard
              product={product}
              locale={locale}
              fill
              overlayDetails
              className="rounded-[1.35rem]"
            />
          </div>
        ))}
      </motion.div>
    </div>
  );
}

function SwipeScene({
  product,
  locale,
  animate,
}: {
  product: ExperiencePreviewProduct;
  locale: string;
  animate: boolean;
}) {
  return (
    <div className="relative flex h-full items-center justify-center px-4">
      <motion.div
        className="relative w-[86%] max-w-[180px]"
        animate={
          animate
            ? {
              x: [0, 72, 72, 0],
              rotate: [0, 8, 8, 0],
              opacity: [1, 1, 0.85, 1],
            }
            : undefined
        }
        transition={
          animate
            ? {
              duration: PREVIEW_ANIMATION_DURATION_S,
              ease: 'easeInOut',
              times: [0, 0.45, 0.7, 1],
            }
            : undefined
        }
      >
        <PreviewMiniCard product={product} locale={locale} />
        <motion.span
          className="absolute top-3 right-3 flex size-8 items-center justify-center rounded-full bg-emerald-500/90 text-white shadow-md"
          animate={animate ? { opacity: [0, 0, 1, 0], scale: [0.8, 0.8, 1, 0.8] } : undefined}
          transition={
            animate
              ? {
                duration: PREVIEW_ANIMATION_DURATION_S,
                ease: 'easeOut',
                times: [0, 0.35, 0.55, 0.75],
              }
              : undefined
          }
          aria-hidden
        >
          <Heart className="size-4 fill-current" />
        </motion.span>
        <motion.span
          className="absolute top-3 left-3 flex size-8 items-center justify-center rounded-full bg-rose-500/90 text-white shadow-md"
          animate={animate ? { opacity: [0, 1, 0, 0], scale: [0.8, 1, 0.8, 0.8] } : undefined}
          transition={
            animate
              ? {
                duration: PREVIEW_ANIMATION_DURATION_S,
                ease: 'easeOut',
                times: [0, 0.2, 0.35, 1],
              }
              : undefined
          }
          aria-hidden
        >
          <ThumbsDown className="size-4" />
        </motion.span>
      </motion.div>
    </div>
  );
}

function BattleScene({
  leftProduct,
  rightProduct,
  locale,
  battlePrompt,
  animate,
}: {
  leftProduct: ExperiencePreviewProduct;
  rightProduct: ExperiencePreviewProduct;
  locale: string;
  battlePrompt: string;
  animate: boolean;
}) {
  return (
    <div className="flex h-full flex-col px-3 py-3">
      <p className="mb-2 text-center text-[10px] font-medium text-muted-foreground">
        {battlePrompt}
      </p>
      <div className="flex min-h-0 flex-1 flex-col gap-2">
        {[leftProduct, rightProduct].map((product, index) => (
          <motion.div
            key={product.id}
            className="min-h-0 flex-1"
            animate={
              animate
                ? {
                  scale: index === 0 ? [1, 1.04, 1, 1] : [1, 1, 1.04, 1],
                  boxShadow: [
                    '0 0 0 0 hsl(var(--primary) / 0)',
                    '0 0 0 2px hsl(var(--primary) / 0.45)',
                    '0 0 0 0 hsl(var(--primary) / 0)',
                    '0 0 0 0 hsl(var(--primary) / 0)',
                  ],
                }
                : undefined
            }
            transition={
              animate
                ? {
                  duration: PREVIEW_ANIMATION_DURATION_S,
                  ease: 'easeInOut',
                  times: index === 0 ? [0, 0.25, 0.5, 1] : [0, 0.5, 0.75, 1],
                }
                : undefined
            }
          >
            <PreviewMiniCard
              product={product}
              locale={locale}
              compact
              fill
              className="h-full rounded-[1.25rem]"
            />
          </motion.div>
        ))}
      </div>
    </div>
  );
}

function PreviewStage({
  scene,
  products,
  locale,
  sceneLabel,
  battlePrompt,
  animate,
}: {
  scene: PreviewScene;
  products: ExperiencePreviewProduct[];
  locale: string;
  sceneLabel: string;
  battlePrompt: string;
  animate: boolean;
}) {
  return (
    <div className="relative mx-auto w-full max-w-[220px]">
      <div className="overflow-hidden rounded-[1.0rem] border border-border/70 bg-muted/40 shadow-[0_20px_50px_-24px_rgba(0,0,0,0.45)]">
        <div className="flex items-center justify-between border-b border-border/60 bg-background/80 px-3 py-2">
          <span className="inline-flex items-center gap-1 text-[10px] font-medium text-primary">
            <Sparkles className="size-3" aria-hidden />
            {sceneLabel}
          </span>
          {scene === 'battle' ? <Swords className="size-3 text-muted-foreground" aria-hidden /> : null}
        </div>
        <div className="relative aspect-[3/5] bg-gradient-to-b from-background to-muted/60">
          <AnimatePresence mode="wait">
            <motion.div
              key={scene}
              className="absolute inset-0"
              initial={animate ? { opacity: 0, y: 8 } : false}
              animate={{ opacity: 1, y: 0 }}
              exit={animate ? { opacity: 0, y: -8 } : undefined}
              transition={{ duration: SCENE_TRANSITION_DURATION_S, ease: 'easeOut' }}
            >
              {scene === 'feed' ? (
                <FeedScene products={products} locale={locale} animate={animate} />
              ) : null}
              {scene === 'swipe' ? (
                <SwipeScene product={products[0]} locale={locale} animate={animate} />
              ) : null}
              {scene === 'battle' ? (
                <BattleScene
                  leftProduct={products[0]}
                  rightProduct={products[1] ?? products[0]}
                  locale={locale}
                  battlePrompt={battlePrompt}
                  animate={animate}
                />
              ) : null}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

/**
 * Dynamic catalog banner previewing the Experience Feed with real store products.
 */
export function ExperienceFeedPreviewBanner({
  normalizedLocale,
  publicStoreId,
  locale,
  products,
  className,
}: ExperienceFeedPreviewBannerProps) {
  const t = useTranslations('catalog.experience');
  const motionReduced = useReducedMotion();
  const systemReducedMotion = useSyncExternalStore(
    subscribeToReducedMotion,
    getReducedMotionSnapshot,
    () => false
  );
  const reduceMotion = motionReduced ?? systemReducedMotion;

  const previewProducts = useMemo(
    () => selectExperiencePreviewProducts(products),
    [products]
  );
  const [sceneIndex, setSceneIndex] = useState(0);
  const scene = SCENES[sceneIndex];
  const experiencePath = `/${normalizedLocale}/${publicStoreId}/experience`;

  const sceneLabels = useMemo(
    () => ({
      feed: t('previewSceneFeed'),
      swipe: t('previewSceneSwipe'),
      battle: t('previewSceneBattle'),
    }),
    [t]
  );

  useEffect(() => {
    if (reduceMotion) return undefined;

    let cancelled = false;
    let timeoutId: number;

    const scheduleNext = (index: number) => {
      timeoutId = window.setTimeout(() => {
        if (cancelled) return;
        const nextIndex = (index + 1) % SCENES.length;
        setSceneIndex(nextIndex);
        scheduleNext(nextIndex);
      }, getSceneDurationMs(SCENES[index]));
    };

    scheduleNext(sceneIndex);

    return () => {
      cancelled = true;
      window.clearTimeout(timeoutId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- timer chain starts from mount index only
  }, [reduceMotion]);

  return (
    <section
      className={cn(
        'overflow-hidden rounded-4xl border border-primary/20 bg-gradient-to-br from-primary/8 via-background to-background p-4 sm:p-5',
        className
      )}
      aria-label={t('previewBannerTitle')}
    >
      <div className="grid items-center gap-5 md:grid-cols-[minmax(0,1fr)_220px] lg:grid-cols-[minmax(0,1fr)_240px]">
        <div className="min-w-0 space-y-3">
          <div className="space-y-1">
            <p className="text-xs font-semibold uppercase tracking-wide text-primary">
              {t('previewBannerEyebrow')}
            </p>
            <h2 className="text-lg font-semibold text-foreground sm:text-xl">
              {t('previewBannerTitle')}
            </h2>
            <p className="max-w-xl text-sm text-muted-foreground sm:text-base">
              {t('previewBannerSubtitle')}
            </p>
          </div>
          <Button asChild className="w-full sm:w-auto">
            <Link href={experiencePath}>{t('previewBannerCta')}</Link>
          </Button>
        </div>

        <PreviewStage
          scene={scene}
          products={previewProducts}
          locale={locale}
          sceneLabel={sceneLabels[scene]}
          battlePrompt={t('previewBattlePrompt')}
          animate={!reduceMotion}
        />
      </div>
    </section>
  );
}
